import React, { useState, useEffect } from 'react';
import { getSeats, selectSeat, placeBid } from '../services/api';
import { Check, AlertCircle, RefreshCw } from 'lucide-react';

const SeatMap = ({ rideId, requestId, biddingEnabled, bidIncrementMin = 50, onSeatSelected }) => {
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedSeat, setSelectedSeat] = useState(null); // Seat object for modal
  const [bidAmount, setBidAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSeats();
  }, [rideId]);

  const fetchSeats = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getSeats(rideId);
      if (res.success) {
        setSeats(res.seats || []);
      } else {
        setError(res.error || 'Failed to fetch seats');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load seats.');
    } finally {
      setLoading(false);
    }
  };

  const handleSeatClick = (seat) => {
    setError('');
    setSuccessMsg('');

    // If it's your own seat, do nothing
    if (Number(seat.occupant_request_id) === Number(requestId)) {
      return;
    }

    setSelectedSeat(seat);
    if (seat.status === 'occupied') {
      const currentPrice = Number(seat.final_price || seat.base_price);
      const minBid = currentPrice + Number(bidIncrementMin);
      setBidAmount(minBid.toString());
    } else {
      setBidAmount('');
    }
  };

  const handleConfirmSelect = async () => {
    if (!selectedSeat) return;
    setActionLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await selectSeat(rideId, selectedSeat.seat_code, requestId);
      if (res.success) {
        setSuccessMsg(res.message || `Seat ${selectedSeat.seat_code} selected!`);
        setSelectedSeat(null);
        await fetchSeats();
        if (onSeatSelected) onSeatSelected();
      } else {
        setError(res.error || 'Failed to select seat');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to select seat.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlaceBid = async () => {
    if (!selectedSeat) return;
    const bidVal = parseFloat(bidAmount);
    const currentPrice = Number(selectedSeat.final_price || selectedSeat.base_price);
    const minBid = currentPrice + Number(bidIncrementMin);

    if (isNaN(bidVal) || bidVal < minBid) {
      setError(`Minimum bid must be at least Rs. ${minBid}`);
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await placeBid(rideId, selectedSeat.seat_code, bidVal, requestId);
      if (res.success) {
        setSuccessMsg(res.message || 'Bid placed successfully!');
        setSelectedSeat(null);
        await fetchSeats();
        if (onSeatSelected) onSeatSelected();
      } else {
        setError(res.error || 'Failed to place bid');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to place bid.');
    } finally {
      setActionLoading(false);
    }
  };

  // Helper to format seat code for humans
  const formatSeatCode = (code) => {
    return code
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-2 text-slate-400">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
        <p className="text-sm">Loading seat map...</p>
      </div>
    );
  }

  // Find seats by code
  const getSeatByCode = (code) => seats.find(s => s.seat_code === code);

  const frontLeft = getSeatByCode('front-left');
  const backLeft = getSeatByCode('back-left');
  const backMiddle = getSeatByCode('back-middle');
  const backRight = getSeatByCode('back-right');

  const renderSeat = (seat, label) => {
    if (!seat) return null;

    const isMine = Number(seat.occupant_request_id) === Number(requestId);
    const isOccupied = seat.status === 'occupied';
    const isReserved = seat.status === 'reserved';
    const isAvailable = seat.status === 'available';

    let bgClass = 'bg-slate-700 hover:bg-slate-600 border-slate-600';
    let textClass = 'text-white';
    let iconEl = null;

    if (isMine) {
      bgClass = 'bg-emerald-600 border-emerald-500 hover:bg-emerald-500';
      textClass = 'text-white font-bold';
      iconEl = <Check className="w-4 h-4 mt-1" />;
    } else if (isReserved) {
      bgClass = 'bg-amber-600 border-amber-500 hover:bg-amber-500 cursor-not-allowed';
      textClass = 'text-white';
    } else if (isOccupied) {
      bgClass = 'bg-slate-600 border-slate-500 hover:bg-slate-500';
      // If bidding enabled, allow clicking to bid
      if (!biddingEnabled) {
        bgClass += ' cursor-not-allowed opacity-75';
      }
      const initial = seat.occupant_name ? seat.occupant_name.charAt(0).toUpperCase() : 'O';
      iconEl = (
        <div className="w-6 h-6 flex items-center justify-center bg-slate-800 text-slate-300 rounded-full text-xs font-semibold mt-1">
          {initial}
        </div>
      );
    } else if (isAvailable) {
      bgClass = 'bg-emerald-800 hover:bg-emerald-700 border-emerald-700';
      textClass = 'text-emerald-200';
    }

    return (
      <button
        onClick={() => (isMine || (isOccupied && !biddingEnabled) || isReserved) ? null : handleSeatClick(seat)}
        disabled={isMine || (isOccupied && !biddingEnabled) || isReserved}
        className={`flex flex-col items-center justify-center w-full min-h-[80px] p-2 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${bgClass} ${textClass}`}
      >
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
        <span className="text-xs opacity-75 mt-0.5">Rs. {Math.round(seat.final_price || seat.base_price)}</span>
        {iconEl}
        {isReserved && <span className="text-[9px] uppercase tracking-wider text-amber-200 mt-1 font-semibold">Bid Pending</span>}
      </button>
    );
  };

  return (
    <div className="w-full max-w-sm mx-auto bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-6">
      <div className="text-center">
        <h4 className="text-base font-semibold text-white">Select Your Seat</h4>
        <p className="text-xs text-slate-400 mt-1">
          {biddingEnabled ? 'Bidding is enabled for occupied seats.' : 'Choose from available seats.'}
        </p>
      </div>

      {error && (
        <div className="flex items-center space-x-2 bg-red-950/50 border border-red-950 text-red-400 p-3 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="flex items-center space-x-2 bg-emerald-950/50 border border-emerald-950 text-emerald-400 p-3 rounded-lg text-xs">
          <Check className="w-4 h-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Car Interior Map */}
      <div className="relative border border-slate-700 rounded-3xl p-4 bg-slate-950 shadow-inner">
        {/* Windshield & Steering Wheel Row */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-4">
          <div className="w-1/2 pr-2">
            {renderSeat(frontLeft, 'Front Passenger')}
          </div>
          
          <div className="w-1/2 pl-2 flex flex-col items-center justify-center min-h-[80px] bg-slate-900/40 rounded-xl border border-slate-800 text-slate-500 select-none">
            <span className="text-[10px] uppercase font-bold tracking-wider mb-1">Driver</span>
            <div className="text-2xl">🚗</div>
          </div>
        </div>

        {/* Back Seats Row */}
        <div className="grid grid-cols-3 gap-2">
          {renderSeat(backLeft, 'Back L')}
          {renderSeat(backMiddle, 'Back M')}
          {renderSeat(backRight, 'Back R')}
        </div>
      </div>

      {/* Seat Legend */}
      <div className="flex flex-wrap justify-center gap-4 text-[10px] text-slate-400 pt-2 border-t border-slate-800/60">
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 rounded bg-emerald-800 border border-emerald-700"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 rounded bg-slate-600 border border-slate-500"></div>
          <span>Occupied</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 rounded bg-emerald-600 border border-emerald-500"></div>
          <span>Your Seat</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 rounded bg-amber-600 border border-amber-500"></div>
          <span>Bid Active</span>
        </div>
      </div>

      {/* Interactive Action Modals */}
      {selectedSeat && (
        <div className="border border-slate-800 bg-slate-950 p-4 rounded-xl space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="flex justify-between items-start">
            <div>
              <h5 className="text-sm font-semibold text-white">
                {selectedSeat.status === 'available' ? 'Confirm Seat Selection' : 'Bid on Occupied Seat'}
              </h5>
              <p className="text-xs text-slate-400 mt-0.5">
                Seat: {formatSeatCode(selectedSeat.seat_code)}
              </p>
            </div>
            <button
              onClick={() => setSelectedSeat(null)}
              className="text-xs text-slate-500 hover:text-slate-400"
            >
              Cancel
            </button>
          </div>

          {selectedSeat.status === 'available' ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-300">
                Would you like to select this seat for Rs. {Math.round(selectedSeat.final_price || selectedSeat.base_price)}?
              </p>
              <button
                disabled={actionLoading}
                onClick={handleConfirmSelect}
                className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Selecting...' : 'Confirm'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-300">
                This seat is taken by <strong className="text-white">{selectedSeat.occupant_name || 'another rider'}</strong>.
                Place a bid to request this seat. If they agree to swap, you will get this seat.
              </p>
              
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                  Bid Amount (Rs.)
                </label>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg py-2 px-3 text-white text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none"
                  placeholder={`Min bid: Rs. ${Number(selectedSeat.final_price || selectedSeat.base_price) + Number(bidIncrementMin)}`}
                />
                <span className="text-[9px] text-slate-500">
                  Must be at least Rs. {Number(selectedSeat.final_price || selectedSeat.base_price) + Number(bidIncrementMin)} (Rs. {bidIncrementMin} increment)
                </span>
              </div>

              <button
                disabled={actionLoading}
                onClick={handlePlaceBid}
                className="w-full py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Placing Bid...' : 'Place Bid'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeatMap;
